import { Link } from 'react-router-dom';
import { toFullUrl } from '../../config/api';
import { Music } from 'lucide-react';
import { Button } from '../ui/button';

export function FreeMediaSection({ freeMedia, siteSettings, isVisible }) {
  if (!isVisible('free_media') || freeMedia.length === 0) return null;

  // Group by album
  const albumGroups = {};
  const singles = [];
  freeMedia.forEach(item => {
    if (item.album_name) {
      if (!albumGroups[item.album_name]) albumGroups[item.album_name] = [];
      albumGroups[item.album_name].push(item);
    } else {
      singles.push(item);
    }
  });
  Object.keys(albumGroups).forEach(k => albumGroups[k].sort((a, b) => (a.track_number || 0) - (b.track_number || 0)));
  const sortedAlbumNames = Object.keys(albumGroups).sort((a, b) => {
    const yearA = albumGroups[a][0]?.release_date || '9999';
    const yearB = albumGroups[b][0]?.release_date || '9999';
    if (yearA !== yearB) return yearA.localeCompare(yearB);
    const orderA = albumGroups[a][0]?.album_order ?? 999;
    const orderB = albumGroups[b][0]?.album_order ?? 999;
    return orderA - orderB;
  });

  let shown = 0;
  const limitedAlbumNames = [];
  for (const name of sortedAlbumNames) {
    if (shown >= 5) break;
    limitedAlbumNames.push(name);
    shown++;
  }
  const limitedSingles = singles.slice(0, Math.max(0, 5 - shown));

  return (
    <section className="border-t border-[#262626] py-16 sm:py-24 px-4 sm:px-8" data-testid="free-media-section" id="free-media">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="overline text-[var(--primary,#FF3B30)] mb-2">{siteSettings?.section_titles?.free_media_label || 'FREE'}</p>
            <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter">
              {siteSettings?.section_titles?.free_media || 'FREE MEDIA'}
            </h2>
          </div>
          <Music className="w-8 h-8 text-[var(--accent,#DAA520)]" />
        </div>

        <div className="space-y-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {limitedAlbumNames.map((albumName) => {
              const tracksInAlbum = albumGroups[albumName];
              const year = tracksInAlbum[0]?.release_date || '';
              return (
                <Link to="/free-media" key={albumName} className="group" data-testid={`free-album-${albumName}`}>
                  <div className="aspect-square bg-[#0A0A0A] border border-[#262626] overflow-hidden group-hover:border-[var(--accent,#DAA520)] transition-colors">
                    {tracksInAlbum[0]?.cover_image ? (
                      <img src={toFullUrl(tracksInAlbum[0].cover_image)} alt={albumName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-10 h-10 text-[#262626]" />
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    <h3 className="font-cabinet font-bold uppercase text-sm truncate group-hover:text-[var(--accent,#DAA520)] transition-colors">{albumName}</h3>
                    <p className="text-[#666] text-xs">{tracksInAlbum.length} track{tracksInAlbum.length > 1 ? 's' : ''}{year ? ` · ${year}` : ''}</p>
                  </div>
                </Link>
              );
            })}
            {limitedSingles.map((item) => (
              <Link to="/free-media" key={item.id} className="group" data-testid={`free-media-${item.id}`}>
                <div className="aspect-square bg-[#0A0A0A] border border-[#262626] overflow-hidden group-hover:border-[var(--accent,#DAA520)] transition-colors">
                  {item.cover_image ? (
                    <img src={toFullUrl(item.cover_image)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-10 h-10 text-[#262626]" />
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <h3 className="font-cabinet font-bold uppercase text-sm truncate group-hover:text-[var(--accent,#DAA520)] transition-colors">{item.title}</h3>
                  {item.artist && <p className="text-[#666] text-xs">{item.artist}</p>}
                </div>
              </Link>
            ))}
          </div>

          <div className="flex justify-center pt-2">
            <Link to="/free-media" data-testid="view-all-free-media-btn">
              <Button variant="outline" className="rounded-none border-[var(--accent,#DAA520)] text-[var(--accent,#DAA520)] hover:bg-[var(--accent,#DAA520)] hover:text-black px-8 py-3 text-sm font-bold uppercase tracking-wider">
                VIEW ALL {siteSettings?.section_titles?.free_media || 'FREE MEDIA'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
